import { useState, useEffect, useCallback, type MouseEvent } from "react";
import { previewCampaign } from "../lib/emailApi";
import type { CreateCampaignDto, PreviewCampaignResponseDto, StoreEmailDto } from "../types/campaign";

interface PreviewModalProps {
  campaign: CreateCampaignDto;
  stores: StoreEmailDto[];
  onClose: () => void;
}

export default function PreviewModal({ campaign, stores, onClose }: PreviewModalProps) {
  const [storeNumber, setStoreNumber] = useState<string>(
    stores.length > 0 ? String(stores[0].storeNumber) : "",
  );
  const [preview, setPreview] = useState<PreviewCampaignResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPreview = useCallback(async () => {
    if (!storeNumber) return;
    setLoading(true);
    setError(null);
    try {
      const result = await previewCampaign(campaign, storeNumber);
      setPreview(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Preview failed.");
    } finally {
      setLoading(false);
    }
  }, [campaign, storeNumber]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  function handleBackdropClick(e: MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal">
        <div className="modal__header">
          <div>
            <p className="section__eyebrow">Email preview</p>
            <h2>How this email will look for a specific store</h2>
          </div>
          <button className="modal__close" type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal__store-select">
          <label className="field">
            <span>Preview as store</span>
            <select value={storeNumber} onChange={(e) => setStoreNumber(e.target.value)}>
              {stores.length === 0 && <option value="">— no stores loaded —</option>}
              {stores.map((s) => (
                <option key={s.storeNumber} value={String(s.storeNumber)}>
                  #{s.storeNumber} — {s.formalName}
                </option>
              ))}
            </select>
          </label>
          <button
            className="button button--ghost"
            type="button"
            onClick={loadPreview}
            disabled={loading || !storeNumber}
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {error && (
          <div className="status-banner status-banner--error">{error}</div>
        )}

        {preview && (
          <div className="modal__preview-frame">
            <div className="modal__preview-meta">
              <span>To: <strong>{preview.toEmail}</strong></span>
              <span>Store: <strong>{preview.storeName}</strong></span>
            </div>
            <div className="modal__preview-subject">
              <span>Subject</span>
              <strong>{preview.resolvedSubject}</strong>
            </div>
            <div
              className="modal__preview-body"
              dangerouslySetInnerHTML={{ __html: preview.resolvedBody }}
            />
          </div>
        )}

        {!preview && !loading && !error && (
          <p className="modal__empty">Select a store and click Refresh to see the rendered preview.</p>
        )}
      </div>
    </div>
  );
}
